import { Injectable } from "@angular/core";
import { HttpClient, HttpErrorResponse, HttpParams } from "@angular/common/http";
import { AlertService, IAlertType } from "./alert.service";
import { Observable, throwError } from "rxjs";
import { catchError, retry } from "rxjs/operators";
import {
  Companies,
  Positions,
  PositionsList,
  ProjectsComment, RequestList,
  RequestPDF,
  Requests,
  WebserviceResponse
} from "../interfaces/";
import { restBaseUrl } from "../utils/rest-url";

@Injectable({
  providedIn: 'root'
})
export class RequestsStoreService {
  projects: Requests[] = [];
  project: Requests | null = null;
  position: Positions | null = null;
  companies: Companies | null = null;

  private readonly endpoint = restBaseUrl('/requests');
  private searchendpoint = restBaseUrl('/contacts');

  constructor(
      private http: HttpClient,
      private alertDispatcher: AlertService
  ) {
  }

  private handleError(error: HttpErrorResponse){
    let errorMessage = 'Unknown error!';
    if (error.error instanceof ErrorEvent) {
      // Client-side errors
      errorMessage = `Client-side error: ${error.error.message}`;
    } else {
      // Server-side errors
      errorMessage = `Server-side error code: ${error.status} - message: ${error.message}`;
    }
    // window.alert(errorMessage);

    this.alertDispatcher.show({
      message: errorMessage,
      clrAlertType: IAlertType.DANGER
    }, 5000);

    // return throwError(errorMessage); deprecated
    return throwError(() => new Error(errorMessage));
  }

  // Main CRUD Services

  public createRequest(req: Request):Observable<WebserviceResponse<any>> {
    return this.http.post<WebserviceResponse<any>>(
        `${this.endpoint}/add_requests`,
        req,
        { responseType: 'json'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public deleteRequest(projectID: string, requestID: number) {
    return this.http.delete(
        `${this.endpoint}/delete_requests?project_number=${projectID}&request_id=${requestID}`,
        { responseType: 'text'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public updateRequest(r: Request, requestID: number):Observable<WebserviceResponse<any>>{
    return this.http.put<WebserviceResponse<any>>(
        `${this.endpoint}/update_requests?reqid=${requestID}`,
        r,
        { responseType: 'json'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public updateRequestTotal(projectID: string, requestID: number, total: number){
    return this.http.put(
        `${this.endpoint}/update_purchasing_price_selling_price?project_id=${projectID}&request_id=${requestID}&purchasing_price=${total}&selling_price=0`,
        { responseType: 'text'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public updateComment(rid: number, pid: string, comment: ProjectsComment) {
    return this.http.put(
        `${this.endpoint}/update_comment?request_id=${rid}&project_id=${pid}`,
        comment,
        { responseType: 'json'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public updatePDFValue(r: RequestPDF) {
    return this.http.put(
        `${this.endpoint}/update_pdf_value`,
        r,
        { responseType: 'json'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public getRequests(projectID: string, requestID: number, status: string): Observable<WebserviceResponse<Requests[]>> {
    const options = new HttpParams({
      fromObject: {
        project_number: projectID,
        request_id: requestID,
        status: status
      }
    });
    return this.http.get<WebserviceResponse<Requests[]>>(this.endpoint + '/get_requests',
        { params: options }).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  // Request Status
  public updateRequestStatus(prid: string, reqid: number, status: string){
    return this.http.put(
        `${this.endpoint}/update_status?project_id=${prid}&request_id=${reqid}&status=${status}`,
        { responseType: 'text'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  // Request Position Services
  public getRequestsPositions(projectID: string, requestID: number, positionID: string, partID: number, positions_status: string): Observable<WebserviceResponse<RequestList[]>> {
    const options = new HttpParams({
      fromObject: {
        project_number: projectID,
        requests_id: requestID,
        position: positionID,
        part: partID,
        status: positions_status
      }
    });
    return this.http.get<WebserviceResponse<RequestList[]>>(this.endpoint + '/get_requests_position',
        { params: options }).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public updateRequestsPosition(position: PositionsList) {
    return this.http.put(
        `${this.endpoint}/update_requests_position`,
        position,
        { responseType: 'json'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }


  public deleteRequestsPosition(projectID: string, requestID: number, positionID: number, partID: number) {
    return this.http.delete(
        `${this.endpoint}/delete_requests_position?project_number=${projectID}&request_id=${requestID}&position=${positionID}&part=${partID}`,
        { responseType: 'text'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public addSingleProjectPosition(projectID: string, requestID: number, positionID: number, partID: number) {
    return this.http.post(
        `${this.endpoint}/add_single_request_position?project_id=${projectID}&request_id=${requestID}&part=${partID}&position=${positionID}`,
        { responseType: 'text'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  // Helpers

  public getRequestsBySupplier(companyID: number): Observable<WebserviceResponse<Requests[]>> {
    const options = new HttpParams({
      fromObject: {
        reqcid: companyID
      }
    });
    return this.http.get<WebserviceResponse<Requests[]>>(this.endpoint + '/get_project_by_supplier',
        { params: options }).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public searchCompany(searchterm: any) : Observable<WebserviceResponse<Companies[]>> {
    return this.http.get<WebserviceResponse<Companies[]>>(
        `${this.searchendpoint}/search_company?name=${encodeURIComponent(searchterm)}&count=0`).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }
}
